import { useQuery } from '@tanstack/react-query';

import type {
  CountryRegion,
  CountryRegionsResponse,
} from '@/app/api/countries/[countryCode]/route';
import type {
  CountryListItem,
  CountryListResponse,
} from '@/app/api/countries/route';

export type { CountryListItem, CountryRegion };

export async function fetchCountries(): Promise<CountryListResponse> {
  const response = await fetch('/api/countries');

  if (!response.ok) {
    throw new Error('Failed to fetch countries', {
      cause: new Error(
        `/api/countries responded with status ${response.status}`,
      ),
    });
  }

  return response.json();
}

export async function fetchCountryRegions(
  countryCode: string,
): Promise<CountryRegionsResponse> {
  const path = `/api/countries/${encodeURIComponent(countryCode)}`;
  const response = await fetch(path);

  if (!response.ok) {
    throw new Error('Failed to fetch regions', {
      cause: new Error(`${path} responded with status ${response.status}`),
    });
  }

  return response.json();
}

/**
 * Shippable countries from ecom-content, as filtered by the content service
 */
export function useCountriesList(): {
  isError: boolean;
  isLoading: boolean;
  data: CountryListItem[];
} {
  const { data, isError, isLoading } = useQuery({
    queryKey: ['countries'],
    queryFn: fetchCountries,
    staleTime: Infinity,
  });

  return { isError, isLoading, data: data?.countries ?? [] };
}

/**
 * List of all regions for a specific country
 * Empty for the majority of countries, which have none; an unknown country
 * code is an error rather than an empty result
 */
export function useCountryRegions(countryCode: string): {
  isError: boolean;
  isLoading: boolean;
  data: CountryRegion[];
} {
  const { data, isError, isLoading } = useQuery({
    queryKey: ['countryRegions', countryCode],
    queryFn: () => fetchCountryRegions(countryCode),
    enabled: Boolean(countryCode),
    staleTime: Infinity,
  });

  return { isError, isLoading, data: data?.regions ?? [] };
}
